import { CookieKey, MasterTranslation, NewsletterOptInSource } from '@/constants';
import { triggerHook, updateUserNotifications } from '@/fetchers';
import { useGaEventBaseParams, useGeo, useGlobalState } from '@/hooks';
import { LoginMethod } from '@/interfaces';
import { useEmailSyncContext } from '@/modules/pmt/context';
import { checkPageWithLanguageCode } from '@/utils';
import Cookies from 'js-cookie';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import router from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import {
  ButtonGroup,
  FullSizeBtn,
  GhostBtn,
  SuccessImageWrapper,
  SuccessSubtext,
  SuccessTitle,
  SuccessWrapper,
} from './Success.styles';

interface SuccessProps {
  onClose?: () => void;
  newsletterSubText?: string;
  subPmcNewsletterText?: string;
  zapierHookId?: string;
}

const Success = ({ onClose, newsletterSubText, subPmcNewsletterText, zapierHookId }: SuccessProps) => {
  const { t, userInfo, isFollowingLinkedIn } = useGlobalState();
  const { geoInfo } = useGeo();
  const [isSubscribing, setIsSubscribing] = useState<boolean>(false);
  const { isEmailSyncAllowed } = useEmailSyncContext();
  const getGaEventBaseParams = useGaEventBaseParams();

  const pathname = usePathname();
  const isAccountPage = checkPageWithLanguageCode(pathname, 'account');

  const isNewsletterScreen = isEmailSyncAllowed && !userInfo?.newsletterOptIn;

  const title = isNewsletterScreen ? subPmcNewsletterText : t(MasterTranslation.SuccessMainText);
  const subText = isNewsletterScreen ? newsletterSubText : t(MasterTranslation.SuccessSubText);
  const imgProps = {
    width: isNewsletterScreen ? 221 : 320,
    height: isNewsletterScreen ? 162 : 177,
    src: isNewsletterScreen ? '/images/onboard-success.svg' : '/images/about-us-for-consumers.svg',
  };

  const handleCloseSuccessScreen = useCallback(() => {
    if (userInfo?.authenticationMethod !== LoginMethod.Email) {
      router.reload();
    }
    onClose?.();
  }, [userInfo, onClose]);

  const onSubscribeNewsletter = useCallback(async () => {
    if (isSubscribing) return;

    try {
      setIsSubscribing(true);

      const gaEventBaseParams = getGaEventBaseParams();
      const res = await updateUserNotifications({
        newsletterOptIn: true,
        ...gaEventBaseParams,
      });

      if (!res?.data || !zapierHookId) return;

      await triggerHook({
        hookId: zapierHookId,
        data: {
          firstName: userInfo?.firstName,
          lastName: userInfo?.lastName,
          email: userInfo?.email,
          authenticationMethod: userInfo?.authenticationMethod,
          profilePictureUrl: userInfo?.profilePictureUrl,
          verificationStatus: true,
          newsletterOptIn: 'Yes',
          newsletterOptInSource: NewsletterOptInSource.PMT_WEBSITE,
          linkedInFollowStatus: isFollowingLinkedIn,
          userType: userInfo?.userType,
          country: userInfo?.country ?? geoInfo?.country,
          emailSyncOptIn: userInfo?.emailSyncOptIn,
        },
      });

      // Close the popup after subscribing to the newsletter
      handleCloseSuccessScreen();
    } catch (err) {
      console.error('Subscribe newsletter failed', err);
    } finally {
      setIsSubscribing(false);
    }
  }, [userInfo, zapierHookId, geoInfo, isFollowingLinkedIn, isSubscribing, handleCloseSuccessScreen]);

  useEffect(() => {
    Cookies.remove(CookieKey.PmtVerificationStatus);
  }, []);

  const renderButtons = () => {
    if (isNewsletterScreen) {
      return (
        <ButtonGroup>
          <FullSizeBtn onClick={onSubscribeNewsletter} disabled={isSubscribing}>
            {t(MasterTranslation.SubscribePMCNewsletter)}
          </FullSizeBtn>
          <GhostBtn onClick={handleCloseSuccessScreen}>{t(MasterTranslation.SkipPMCNewsletter)}</GhostBtn>
        </ButtonGroup>
      );
    }

    if (isAccountPage) return null;

    return (
      <FullSizeBtn onClick={handleCloseSuccessScreen} truncate>
        {t(MasterTranslation.ViewMyParcels)}
      </FullSizeBtn>
    );
  };

  return (
    <SuccessWrapper>
      <div>
        <SuccessTitle>{title}</SuccessTitle>
        <SuccessSubtext>{subText}</SuccessSubtext>
      </div>
      <SuccessImageWrapper>
        <Image {...imgProps} alt='Success Image' />
      </SuccessImageWrapper>
      {renderButtons()}
    </SuccessWrapper>
  );
};

export default Success;
